import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { ReportQueuePage } from "./pages/ReportQueuePage";
import { ReportDetailPage } from "./pages/ReportDetailPage";
import { ReportUploadPage } from "./pages/ReportUploadPage";
import { ReportOrdersPage } from "./pages/ReportOrdersPage";
import { AlertInboxPage } from "./pages/AlertInboxPage";
import { TaskBoardPage } from "./pages/TaskBoardPage";
import { AdminConsolePage } from "./pages/AdminConsolePage";
import { AnalyticsDashboardPage } from "./pages/AnalyticsDashboardPage";
import { SuperAdminConsolePage } from "./pages/SuperAdminConsolePage";
import { PatientManagementPage } from "./pages/PatientManagementPage";
import { SSOCallbackPage } from "./pages/SSOCallbackPage";

// Layout
import { AppLayout } from "./components/AppLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* SSO Callback Route (public - handles token validation) */}
      <Route path="/sso/callback" element={<SSOCallbackPage />} />

      {/* Protected Routes with AppLayout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard redirects to Reports (combined view) */}
        <Route path="/dashboard" element={<Navigate to="/reports" replace />} />
        <Route path="/reports" element={<ReportQueuePage />} />
        <Route path="/reports/upload" element={<ReportUploadPage />} />
        <Route path="/reports/:reportId" element={<ReportDetailPage />} />
        <Route path="/orders" element={<ReportOrdersPage />} />
        <Route path="/alerts" element={<AlertInboxPage />} />
        <Route path="/tasks" element={<TaskBoardPage />} />
        <Route path="/analytics" element={<AnalyticsDashboardPage />} />
        <Route path="/patients" element={<PatientManagementPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "org_admin"]}>
              <AdminConsolePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <SuperAdminConsolePage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/reports" replace />} />
      <Route path="*" element={<Navigate to="/reports" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
